import { scriptUtils } from '@sendible/common';
import { publish } from '@sendible/event-manager';
import EVENTS from '@sendible/event-manager/src/event-types';

interface CanvaCallbackData {
  url: string;
  designId: string;
  exportUrl: string;
}

declare const CANVA_API_KEY: string;
declare global {
  interface Window {
    canvaCallback: (obj: CanvaCallbackData) => void;
    Canva?: {
      DesignButton?: {
        initialize?: (options: { apiKey: string }) => void;
      };
    };
  }
}

export const initializeCanvaGlobal = (): void => {
  window.canvaCallback = (obj: CanvaCallbackData) => {
    publish(EVENTS.CANVA_IMAGE_CALLBACK, { exportUrl: obj.exportUrl });
  };
};

export const loadCanvaScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector('.canva-script');

    if (existingScript) {
      if (window.Canva) {
        resolve();

        return;
      }

      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Canva script')));

      return;
    }

    initializeCanvaGlobal();

    scriptUtils
      .addScriptToBody('https://sdk.canva.com/v2/beta/api.js', {
        className: 'canva-script',
        async: true,
      })
      .then(() => {
        resolve();
      })
      .catch(() => {
        reject(new Error('Failed to load Canva script'));
      });
  });
};

export const createCanvaPlaceholder = (): void => {
  const existingPlaceholder = document.getElementById('canva-placeholder-v2');

  if (existingPlaceholder) {
    return;
  }

  const placeholder = document.createElement('div');

  placeholder.id = 'canva-placeholder-v2';
  placeholder.style.display = 'none';

  const designs = [
    { type: 'TumblrGraphic', label: 'Design a blog graphic' },
    { type: 'FacebookCover', label: 'Design a Facebook cover' },
    { type: 'FacebookPost', label: 'Design a Facebook post' },
    { type: 'PinterestGraphic', label: 'Design a pin' },
    { type: 'TwitterPost', label: 'Design a Twitter post' },
    { type: 'InstagramPost', label: 'Design an Instagram post' },
  ];

  designs.forEach((design) => {
    const span = document.createElement('span');

    span.setAttribute('data-design-type', design.type);
    span.setAttribute('data-api-key', CANVA_API_KEY);
    span.className = 'canva-design-button';
    span.setAttribute('data-on-publish-callback', 'canvaCallback');
    span.textContent = design.label;
    placeholder.appendChild(span);
  });

  document.body.appendChild(placeholder);
};

export const triggerCanva = (canvaType: string): void => {
  const button = document.querySelector(`[data-design-type="${canvaType}"]`) as HTMLElement;

  if (button) {
    // NOTE: Simulate a real click event
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });

    button.dispatchEvent(clickEvent);
  }
};
